// Customers > Linked Accounts - customer accounts that belong to one person or one household.
//
// Greg, 2026-09-29: Joseph Tufano was sent the free VIP win-back at his old email while paying for
// a membership under a new one. BigCommerce gives every email its own account, so every "is this
// person a VIP?" check was really "is this ACCOUNT a VIP?". The nightly scan
// (lib/linked-accounts.js) groups accounts that share a phone, billing address or email on their
// profiles OR their orders; this page is where a person confirms, splits or rejects those groups.
//
// ⚠️ SUGGESTED GROUPS ALREADY SUPPRESS VIP OFFERS. The review here refines them, it does not turn
// them on. Marking a group Not related is what stops it counting.
//
// ⚠️ Handlers return `groups` / `counts`, never `rows` - neonQuery() unwraps a top-level `rows`.
import React, { useCallback, useEffect, useState } from 'react';
import { Link2, RefreshCw, Search, X } from 'lucide-react';
import { neonQuery } from '../services/api';
import { getCurrentUser } from '../services/auth';

const SIGNAL = {
  phone: 'Same phone',
  addr: 'Same billing address + last name',
  email: 'Same email used',
  namezip: 'Same name + zip',
  handle: 'Similar email handle',
};
const TABS = [
  { v: 'suggested', label: 'Needs review' },
  { v: 'confirmed', label: 'Confirmed' },
  { v: 'rejected', label: 'Not related' },
];
const FOCUS = [
  { v: 'conflicts', label: 'Active + lapsed VIP' },
  { v: 'vip', label: 'Any VIP' },
  { v: 'all', label: 'Everything' },
];
const PAGE = 25;

const letter = i => String.fromCharCode(65 + i);
const money = n => '$' + Number(n || 0).toLocaleString('en-US');
const fmtDate = d => {
  if (!d) return '';
  const [y, m, dd] = String(d).slice(0, 10).split('-');
  if (!y || !m) return String(d);
  return new Date(Number(y), Number(m) - 1, Number(dd)).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

function Pill({ on, children, onClick, tone = 'navy' }) {
  const c = tone === 'orange' ? 'var(--orange)' : 'var(--navy)';
  return (
    <button type="button" onClick={onClick} style={{
      padding: '4px 11px', fontSize: 12, fontWeight: 600, cursor: 'pointer', borderRadius: 999,
      border: `1px solid ${on ? c : 'var(--gray-200)'}`, background: on ? c : '#fff', color: on ? '#fff' : '#444',
    }}>{children}</button>
  );
}

function Tag({ i }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 20, height: 20,
      borderRadius: 5, background: 'var(--navy)', color: '#fff', fontSize: 11, fontWeight: 700, marginRight: 6, flex: '0 0 auto',
    }}>{letter(i)}</span>
  );
}

function VipBadge({ m }) {
  if (m.vip === 'none') return <span style={{ color: '#888', fontSize: 12 }}>Never VIP</span>;
  const active = m.vip === 'active';
  return (
    <span style={{ fontSize: 12 }}>
      <span style={{
        fontWeight: 700, padding: '2px 8px', borderRadius: 999, marginRight: 6,
        background: active ? '#f8edd3' : '#eceff2', color: active ? '#8a6414' : '#5a6b78',
      }}>{active ? 'Active VIP' : 'Lapsed VIP'}</span>
      <span style={{ color: '#777' }}>
        {active ? `through ${fmtDate(m.vipExpires)}` : `expired ${fmtDate(m.vipExpires)}`}
      </span>
    </span>
  );
}

const btn = (bg, fg = '#fff', border = bg) => ({
  padding: '5px 12px', fontSize: 12, fontWeight: 600, borderRadius: 6, cursor: 'pointer',
  background: bg, color: fg, border: `1px solid ${border}`,
});

function GroupCard({ g, onVerdict, onRemove, onRelink, busy }) {
  const idx = new Map(g.members.map((m, i) => [m.contactId, i]));
  const names = [...new Set(g.members.map(m => m.name))].join(' · ');
  const th = { textAlign: 'left', padding: '7px 10px', fontSize: 11, color: '#777', textTransform: 'uppercase', letterSpacing: '.05em', whiteSpace: 'nowrap', verticalAlign: 'top', width: 90 };
  const td = { padding: '7px 10px', fontSize: 13, verticalAlign: 'top', borderTop: '1px solid var(--gray-100, #f0f2f4)' };
  const rows = [
    ['Email', m => <span style={{ fontFamily: 'ui-monospace, Consolas, monospace', fontSize: 12, wordBreak: 'break-all' }}>{m.email}</span>],
    ['Address', m => m.address || <span style={{ color: '#aaa' }}>none</span>],
    ['Phone', m => m.phone || <span style={{ color: '#aaa' }}>none</span>],
    ['VIP', m => <VipBadge m={m} />],
    ['Orders', m => <span>{m.orders}{m.orders ? <span style={{ color: '#777' }}> · last {fmtDate(m.lastOrder)} · {money(m.spend)}</span> : null}</span>],
    ['Account', m => <span style={{ color: '#777', fontSize: 12 }}>#{m.contactId}{m.created ? ` · created ${fmtDate(m.created)}` : ''}{m.missing ? ' · deleted' : ''}</span>],
  ];
  const guessLabel = g.guess === 'household' ? 'Looks like: household' : 'Looks like: same person';
  const canRemove = g.status !== 'rejected';
  return (
    <div style={{ background: '#fff', border: `1px solid ${g.conflict && g.status === 'suggested' ? 'var(--orange)' : 'var(--gray-200)'}`, borderRadius: 8, marginBottom: 14, overflow: 'hidden' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px 12px', padding: '10px 14px', borderBottom: '1px solid var(--gray-200)' }}>
        <div style={{ fontWeight: 700, fontSize: 14, flex: '1 1 260px', color: 'var(--navy)' }}>{names}</div>
        {g.conflict && <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 999, background: '#fbe9e2', color: 'var(--orange)' }}>Active + lapsed VIP</span>}
        {g.status === 'suggested' && <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 999, background: '#eef2f5', color: '#5a6b78' }}>{guessLabel}</span>}
        {g.status === 'suggested' && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <button type="button" disabled={busy} style={btn('#1f7a4d')} onClick={() => onVerdict(g, 'person')}>Same person</button>
            <button type="button" disabled={busy} style={btn('#9a6a00')} onClick={() => onVerdict(g, 'household')}>Same household</button>
            <button type="button" disabled={busy} style={btn('#fff', '#b3261e', '#e6b3af')} onClick={() => onVerdict(g, 'no')}>Not related</button>
          </div>
        )}
        {g.status === 'confirmed' && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: 12, color: '#555' }}>
            <span>Confirmed <b>{g.kind === 'household' ? 'household' : 'same person'}</b>{g.reviewedBy ? ` by ${g.reviewedBy}` : ''}{g.reviewedOn ? ` on ${g.reviewedOn}` : ''}{g.source === 'manual' ? ' (linked by hand)' : ''}</span>
            <button type="button" disabled={busy} style={btn('#fff', 'var(--navy)', 'var(--gray-200)')}
              onClick={() => onVerdict(g, g.kind === 'household' ? 'person' : 'household')}>
              Change to {g.kind === 'household' ? 'same person' : 'household'}
            </button>
            <button type="button" disabled={busy} style={btn('#fff', '#b3261e', '#e6b3af')} onClick={() => onVerdict(g, 'no')}>Not related</button>
          </div>
        )}
        {g.status === 'rejected' && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: 12, color: '#555' }}>
            <span>Marked not related{g.reviewedBy ? ` by ${g.reviewedBy}` : ''}{g.reviewedOn ? ` on ${g.reviewedOn}` : ''}</span>
            {g.members.length >= 2 && (
              <button type="button" disabled={busy} style={btn('#fff', 'var(--navy)', 'var(--gray-200)')} onClick={() => onRelink(g)}>Link them after all</button>
            )}
          </div>
        )}
      </div>
      {g.note && <div style={{ padding: '8px 14px', background: '#fbf1d9', fontSize: 13, color: '#6b4c00' }}>{g.note}</div>}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 560 }}>
          <thead>
            <tr style={{ background: '#f6f8fa' }}>
              <th style={th} />
              {g.members.map((m, i) => (
                <th key={m.contactId} style={{ ...th, width: 'auto', textTransform: 'none', letterSpacing: 0, fontSize: 13, color: '#222' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <Tag i={i} />{m.name}
                    {canRemove && (
                      <button type="button" title="Not part of this group" disabled={busy} onClick={() => onRemove(g, m)}
                        style={{ border: 0, background: 'transparent', color: '#b3261e', cursor: 'pointer', padding: 2, display: 'inline-flex' }}>
                        <X size={14} />
                      </button>
                    )}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(([h, f]) => (
              <tr key={h}>
                <th scope="row" style={{ ...th, borderTop: '1px solid var(--gray-100, #f0f2f4)' }}>{h}</th>
                {g.members.map(m => <td key={m.contactId} style={td}>{f(m)}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {g.evidence.length > 0 && (
        <div style={{ padding: '10px 14px 14px', display: 'grid', gap: 10 }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: '#777' }}>Why they were grouped</div>
          {g.evidence.map((e, k) => (
            <div key={k} style={{ borderLeft: `3px solid ${e.strong ? 'var(--navy)' : 'var(--gray-200)'}`, paddingLeft: 10, display: 'grid', gap: 4 }}>
              <div style={{ fontSize: 13 }}>
                <b style={{ color: e.strong ? '#222' : '#777' }}>{SIGNAL[e.signal] || e.signal}</b>
                {!e.strong && <span style={{ color: '#999', fontSize: 12 }}> (supporting only)</span>}
                <span style={{ fontFamily: 'ui-monospace, Consolas, monospace', fontSize: 12, marginLeft: 8 }}>{e.value}</span>
              </div>
              {e.where.filter(w => idx.has(w.contactId)).map(w => (
                <div key={w.contactId} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 4, fontSize: 12 }}>
                  <Tag i={idx.get(w.contactId)} />
                  {w.srcs.map(s => s === 'profile'
                    ? <span key={s} style={{ background: '#fbe9e2', color: 'var(--orange)', fontWeight: 700, padding: '1px 7px', borderRadius: 4 }}>Profile</span>
                    : <span key={s} style={{ background: '#eef2f5', fontFamily: 'ui-monospace, Consolas, monospace', padding: '1px 7px', borderRadius: 4 }}>{s.replace(/^order /, '')}</span>)}
                  {w.moreOrders > 0 && <span style={{ color: '#888' }}>+{w.moreOrders} more orders</span>}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function LinkedAccounts({ initialQuery }) {
  const user = getCurrentUser() || {};
  const actor = { adminId: user.id, adminName: user.staffName || user.name || null };
  const roles = Array.isArray(user.roles) ? user.roles : String(user.roles || '').split(',').map(r => r.trim());
  const canConfigure = roles.includes('Admin') || roles.includes('Manager');

  const [stats, setStats] = useState(null);
  // Arriving from Customer Search ("Open in Linked Accounts") lands on that customer's group,
  // whichever tab or focus it sits under.
  const handoff = initialQuery && initialQuery.q ? String(initialQuery.q) : '';
  const [status, setStatus] = useState(handoff ? 'any' : 'suggested');
  const [focus, setFocus] = useState(handoff ? 'all' : 'conflicts');
  const [search, setSearch] = useState(handoff);
  const [query, setQuery] = useState(handoff);
  const [groups, setGroups] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [flash, setFlash] = useState('');
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkText, setLinkText] = useState('');
  const [linkKind, setLinkKind] = useState('person');

  const loadStats = useCallback(async () => {
    try { setStats(await neonQuery('identity_stats', {})); } catch (e) { /* the list still works */ }
  }, []);

  const load = useCallback(async (append = false, offset = 0) => {
    setLoading(true); setErr('');
    try {
      const r = await neonQuery('identity_groups_list', { status, focus, search: query, limit: PAGE, offset });
      setTotal(r.total || 0);
      setGroups(prev => append ? [...prev, ...(r.groups || [])] : (r.groups || []));
    } catch (e) { setErr(e.message || 'Could not load linked accounts.'); }
    setLoading(false);
  }, [status, focus, query]);

  useEffect(() => { load(false, 0); }, [load]);
  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { const t = setTimeout(() => setQuery(search.trim()), 350); return () => clearTimeout(t); }, [search]);

  const say = m => { setFlash(m); setTimeout(() => setFlash(''), 4000); };
  const act = async (fn, done) => {
    setBusy(true); setErr('');
    try {
      const r = await fn();
      if (r && r.error) { setErr(r.error); setBusy(false); return false; }
      if (done) say(done);
      await Promise.all([load(false, 0), loadStats()]);
      setBusy(false);
      return true;
    } catch (e) { setErr(e.message || 'That did not save.'); }
    setBusy(false);
    return false;
  };

  const onVerdict = (g, verdict) => act(
    () => neonQuery('identity_group_review', { groupId: g.id, verdict, ...actor }),
    verdict === 'no' ? 'Marked not related. They will not be linked again.' : `Confirmed as ${verdict === 'person' ? 'the same person' : 'one household'}.`);
  const onRemove = (g, m) => act(
    () => neonQuery('identity_member_remove', { groupId: g.id, contactId: m.contactId, ...actor }),
    `${m.email} taken out of the group.`);
  const onRelink = g => act(
    () => neonQuery('identity_manual_link', { identifiers: g.members.map(m => m.contactId), kind: g.kind || g.guess || 'person', ...actor }),
    'Linked.');
  const onManualLink = () => act(
    () => neonQuery('identity_manual_link', { identifiers: linkText, kind: linkKind, ...actor }),
    'Linked. They now count as one customer for VIP offers.').then(ok => { if (ok) setLinkText(''); });
  const onScan = () => act(async () => {
    const r = await neonQuery('identity_scan_start', {});
    if (r && r.started === false) return { error: r.reason || 'The scan did not start.' };
    return r;
  }, 'Scan started. It takes under a minute; refresh to see the result.');
  const onHouseholds = on => act(() => neonQuery('identity_households_set', { on, ...actor }),
    on ? 'Households now count as linked for VIP offers.' : 'Households no longer count for VIP offers; same-person links still do.');

  const c = (stats && stats.counts) || {};
  const run = stats && stats.lastRun;

  return (
    <div style={{ padding: 24, maxWidth: 1180 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Link2 size={22} color="var(--navy)" />
        <h1 style={{ margin: 0, fontSize: 22, color: 'var(--navy)' }}>Linked Accounts</h1>
      </div>
      <p style={{ color: '#555', maxWidth: 820, marginTop: 6, fontSize: 14 }}>
        Customer accounts that look like one person or one household: they share a phone, a billing address or an email,
        on their profiles or on past orders. Linked accounts count as one customer for VIP offers, so a paying member is
        never sent a win-back under their other email. Groups count as soon as the nightly scan finds them. Reviewing
        them here confirms them, and <b>Not related</b> stops them counting.
      </p>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, margin: '14px 0' }}>
        {[
          ['Needs review', c.suggested, `${c.conflicts || 0} with an active and a lapsed VIP`],
          ['Confirmed', c.confirmed, 'reviewed by someone'],
          ['Not related', c.rejected, 'will not be linked again'],
          ['Accounts linked', c.accounts, 'across review + confirmed'],
        ].map(([label, n, sub]) => (
          <div key={label} style={{ background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 8, padding: '10px 14px', minWidth: 170, flex: '1 1 170px' }}>
            <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--navy)', fontVariantNumeric: 'tabular-nums' }}>{n == null ? '–' : Number(n).toLocaleString('en-US')}</div>
            <div style={{ fontSize: 12, fontWeight: 600 }}>{label}</div>
            <div style={{ fontSize: 11, color: '#888' }}>{sub}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, fontSize: 12, color: '#666', marginBottom: 14 }}>
        <span>
          Last scan: {run ? `${run.started} (${run.trigger})` : 'never'}
          {run && run.running ? ' · running now' : run && run.status === 'failed' ? ' · FAILED' : ''}
          {run && run.note ? ` · ${run.note}` : ''}
        </span>
        <button type="button" disabled={busy || (run && run.running)} onClick={onScan}
          style={{ ...btn('#fff', 'var(--navy)', 'var(--gray-200)'), display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <RefreshCw size={13} /> Run scan now
        </button>
        {canConfigure && stats && (
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            <input id="households-linked" type="checkbox" checked={!!stats.householdsLinked} disabled={busy}
              onChange={e => onHouseholds(e.target.checked)} />
            Households count as linked for VIP offers
          </label>
        )}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        {TABS.map(t => <Pill key={t.v} on={status === t.v} onClick={() => setStatus(t.v)}>{t.label}</Pill>)}
        <span style={{ width: 12 }} />
        {FOCUS.map(f => <Pill key={f.v} tone="orange" on={focus === f.v} onClick={() => setFocus(f.v)}>{f.label}</Pill>)}
        <div style={{ position: 'relative', marginLeft: 'auto' }}>
          <Search size={14} style={{ position: 'absolute', left: 8, top: 8, color: '#999' }} />
          <input id="linked-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Email, name or account #"
            style={{ padding: '6px 10px 6px 28px', border: '1px solid var(--gray-200)', borderRadius: 6, fontSize: 13, width: 240 }} />
        </div>
        <button type="button" onClick={() => setLinkOpen(o => !o)} style={btn('var(--navy)')}>Link accounts by hand</button>
      </div>

      {linkOpen && (
        <div style={{ background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 8, padding: 14, marginBottom: 14, display: 'grid', gap: 8, maxWidth: 640 }}>
          <div style={{ fontSize: 13 }}>Emails or account numbers of the accounts that belong together, one per line. Any groups they are already in are merged.</div>
          <textarea id="manual-link" rows={3} value={linkText} onChange={e => setLinkText(e.target.value)}
            placeholder={'patcalabrese319@gmail.com\nozjat24@gmail.com'}
            style={{ fontFamily: 'ui-monospace, Consolas, monospace', fontSize: 12, padding: 8, border: '1px solid var(--gray-200)', borderRadius: 6 }} />
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <Pill on={linkKind === 'person'} onClick={() => setLinkKind('person')}>Same person</Pill>
            <Pill on={linkKind === 'household'} onClick={() => setLinkKind('household')}>Same household</Pill>
            <button type="button" disabled={busy || !linkText.trim()} onClick={onManualLink} style={btn('#1f7a4d')}>Link them</button>
          </div>
        </div>
      )}

      {flash && <div style={{ padding: '8px 12px', background: '#e2f3ea', color: '#1f7a4d', borderRadius: 6, fontSize: 13, marginBottom: 10 }}>{flash}</div>}
      {err && <div style={{ padding: '8px 12px', background: '#fbe4e2', color: '#b3261e', borderRadius: 6, fontSize: 13, marginBottom: 10 }}>{err}</div>}

      <div style={{ fontSize: 12, color: '#777', marginBottom: 8 }}>
        {loading && !groups.length ? 'Loading…' : `${total.toLocaleString('en-US')} group${total === 1 ? '' : 's'}${query ? ` matching "${query}"` : ''}`}
      </div>
      {!loading && !groups.length && (
        <div style={{ padding: 24, textAlign: 'center', color: '#888', background: '#fff', border: '1px dashed var(--gray-200)', borderRadius: 8 }}>
          Nothing here{focus !== 'all' ? '. Try Everything to see groups without a VIP.' : '.'}
        </div>
      )}
      {groups.map(g => (
        <GroupCard key={g.id} g={g} busy={busy} onVerdict={onVerdict} onRemove={onRemove} onRelink={onRelink} />
      ))}
      {groups.length < total && (
        <button type="button" disabled={loading} onClick={() => load(true, groups.length)} style={{ ...btn('#fff', 'var(--navy)', 'var(--gray-200)'), marginTop: 4 }}>
          {loading ? 'Loading…' : `Show ${Math.min(PAGE, total - groups.length)} more`}
        </button>
      )}
    </div>
  );
}
